import {
  // colorVariants,
  createBaseThemeOptions,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

export const DarkGunvorTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      primary: {
        main: '#0077b6',       // Vibrant blue
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#5c6f7c',       // Muted steel blue
      },
      error: {
        main: '#e74c3c',
      },
      warning: {
        main: '#f39c12',
      },
      info: {
        main: '#48cae4',       // Bright cyan
      },
      success: {
        main: '#2ecc71',
      },
      background: {
        default: '#0a1a2f',    // Deep blue-black
        paper: '#12263a',      // Slightly lighter blue-gray
      },
      text: {
        primary: '#e0f7fa',    // Soft cyan-white
        secondary: '#b0bec5',
      },

      // ✅ Banner palette
      banner: {
        info: '#1c3d5a',
        error: '#3d1f1f',
        text: '#e0f7fa',
        link: '#90caf9',
      },
      errorBackground: '#3d1f1f',
      warningBackground: '#3d2a1f',
      infoBackground: '#1c3d5a',

      // ✅ Navigation palette
      navigation: {
        background: '#12263a',     // Sidebar/nav background
        indicator: '#48cae4',      // Cyan underline
        color: '#b0bec5',          // Default icon/text
        selectedColor: '#e0f7fa', // Selected icon/text
        navItem: {
          hoverBackground: '#1c3d5a', // Hover background
        },
        submenu: {
          background: '#0a1a2f',      // Submenu background
        },
      },
      shape: {
         borderRadius: 20
      }
    },
  }),
  defaultPageTheme: 'home',
  typography: {
    ...defaultTypography,
    // htmlFontSize: 18,
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
      marginBottom: 10
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.5rem',
      marginBottom: 8
    },
  },
  /* below drives the header colors */
  pageTheme: {
    home: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
    documentation: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
    tool: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
    service: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
    library: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
    other: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.curve }),
    app: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
    catalog: genPageTheme({ colors: ['#002b45', '#0077b6'], shape: shapes.wave }),
  },
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme?.palette.navigation.color,
          '&:hover': {
            color: theme?.palette.navigation.indicator,
          },
        }),
      },
    },

    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            color: theme?.palette.navigation.indicator,
          },
        }),
      },
    },

    MuiButton: {
      styleOverrides: {
        containedPrimary: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme?.palette.navigation.indicator,
            color: theme?.palette.navigation.background,
          },
          color: theme?.palette.navigation.color,
          backgroundColor: theme?.palette.navigation.background,
        }),

        containedSecondary: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme?.palette.navigation.indicator,
          },
          color: theme?.palette.navigation.color,
        }),
      },
    },

    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme?.palette.primary.main,
          color: theme?.palette.primary.contrastText,
        }),

        outlined: ({ theme }) => ({
          borderColor: theme?.palette.primary.main,
          color: theme?.palette.primary.contrastText,
        }),

      },
    },
  },
});


export const LightGunvorTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: '#006b72',      // Deep teal
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#00385e',       // Crisp blue
      },
      error: {
        main: '#c0392b',
      },
      warning: {
        main: '#f39c12',
      },
      info: {
        main: '#2980b9',
      },
      success: {
        main: '#485e30',
      },
      background: {
        default: '#f4f6f7',
        paper: '#ffffff',
      },
      text: {
        primary: '#2c3e50',
        secondary: '#7f8c8d',
      },
      banner: {
       info: '#e8f4fd',             // Light blue background
       error: '#fdecea',            // Light red background
       text: '#006b72',             // Rebased main color for banner text
       link: '#005580',             // Brighter blue for links
      },
      errorBackground: '#fdecea',       // Soft red tint
      warningBackground: '#fff4e5',     // Soft amber tint
      infoBackground: '#e8f4fd',       // Soft blue tint
      // ✅ Navigation palette
      navigation: {
        background: '#006b72',     // Teal sidebar/nav background
        indicator: '#00b2be',      // White underline for active tab
        color: '#ffffff',          // Default menu text
        selectedColor: '#00b2be', // Bright blue accent for selected items
        navItem: {
          hoverBackground: '#003b3f', // Hover background
        },
        submenu: {
          background: '#002325',      // Submenu background
        },
      },

    },
  }),
  defaultPageTheme: 'home',
  typography: {
    ...defaultTypography,
    htmlFontSize: 14,
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
      marginBottom: 10
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.5rem',
      marginBottom: 8
    },
  },
  /* below drives the header colors */
  pageTheme: {

    // use primary + info from this theme palette
    home: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    documentation: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    tool: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    service: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    library: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    other: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
    catalog: genPageTheme({ colors: ['#006b72', '#2980b9'], shape: shapes.wave }),
  },
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme?.palette.navigation.color,
          '&:hover': {
            color: theme?.palette.navigation.selectedColor,
          },
        }),
      },
    },

    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            color: theme?.palette.navigation.selectedColor,
          },
        }),
      },
    },

    MuiButton: {
      styleOverrides: {
        containedPrimary: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme?.palette.navigation.selectedColor,
          },
          color: theme?.palette.navigation.color,
          backgroundColor: theme?.palette.navigation.background,
        }),

        containedSecondary: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme?.palette.navigation.selectedColor,
          },
          color: theme?.palette.navigation.color,
        }),
      },
    },

    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme?.palette.primary.main,
          color: theme?.palette.primary.contrastText,
        }),

        outlined: ({ theme }) => ({
          borderColor: theme?.palette.primary.main,
          color: theme?.palette.primary.main,
        }),

      },
    },
  },
});